import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'
import { cache } from '../utils/cache'

interface Customer {
  id: string
  name: string
  domain?: string
  status: string
  primaryEmail?: string
  totalLicenses: number
  totalUsers: number
  hasAzureSubscription: boolean
  lastActivityAt?: string
}

export default function Customers() {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'withProducts'>('withProducts')
  const { getAccessToken } = useAuth()

  useEffect(() => {
    async function fetchCustomers() {
      try {
        setLoading(true)
        setError(null)
        const token = await getAccessToken()
        const data = await api.getCustomers(token, filter)
        setCustomers(data.customers || [])
      } catch (err) {
        console.error('Failed to fetch customers:', err)
        setError('Failed to load customers. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchCustomers()
  }, [filter]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = async () => {
    try {
      setSyncing(true)
      setSyncMessage(null)
      const token = await getAccessToken()
      const result = await api.syncAllCustomerMetrics(token)

      // Check if sync was rejected due to already in progress
      if (result.error === 'Sync already in progress') {
        setSyncMessage(result.message || 'A sync is already in progress. Please wait.')
        return
      }

      if (result.success) {
        const details = [`${result.totalCustomers || 0} customers synced`]

        if (result.successCount !== undefined) {
          details.push(`${result.successCount} successful`)
        }

        if (result.failedCount !== undefined && result.failedCount > 0) {
          details.push(`${result.failedCount} failed`)
        }

        setSyncMessage(`Sync completed: ${details.join(', ')}`)

        // Clear customer cache entries to ensure fresh data after sync
        cache.clear('api:/customers?filter=withProducts')
        cache.clear('api:/customers?filter=all')

        // Refresh customer list with current filter
        const data = await api.getCustomers(token, filter)
        setCustomers(data.customers || [])
      } else {
        setSyncMessage('Sync failed. Please try again.')
      }
    } catch (err: unknown) {
      console.error('Sync failed:', err)
      // Check if this is a 409 conflict error (sync already in progress)
      if (err && typeof err === 'object' && 'message' in err) {
        const errorMessage = (err as { message: string }).message
        if (errorMessage.includes('409') || errorMessage.includes('already in progress')) {
          setSyncMessage('A sync is already in progress. Please wait for it to complete.')
          return
        }
      }
      setSyncMessage('Sync failed. Please try again.')
    } finally {
      setSyncing(false)
    }
  }

  if (loading) {
    return (
      <div className="px-4 sm:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading customers...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-4 sm:px-0">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h2 className="text-2xl font-bold text-gray-900">Customers</h2>
          <p className="mt-2 text-sm text-gray-700">
            Partner Center customers with their relationship status and contact information.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncing ? (
              <>
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Syncing...
              </>
            ) : (
              'Sync from Partner Center'
            )}
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="sm:flex sm:items-center">
          <label htmlFor="filter" className="text-sm font-medium text-gray-700 mr-2">
            Show:
          </label>
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setFilter('withProducts')}
              className={`px-4 py-2 text-sm font-medium border ${
                filter === 'withProducts'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } rounded-l-lg`}
            >
              With Products
            </button>
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium border-t border-b border-r ${
                filter === 'all'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } rounded-r-lg`}
            >
              All Customers
            </button>
          </div>
          <span className="ml-3 text-sm text-gray-500">
            {customers.length} customer{customers.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {syncMessage && (
        <div className="mb-4 rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{syncMessage}</p>
            </div>
          </div>
        </div>
      )}

      {customers.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No customers found.</p>
        </div>
      ) : (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Company Name</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Domain</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Licenses</th>
                      <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Azure</th>
                      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {customers.map((customer) => (
                      <tr key={customer.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium sm:pl-6">
                          <Link
                            to={`/customers/${customer.id}`}
                            className="text-primary-600 hover:text-primary-900 hover:underline"
                          >
                            {customer.name}
                          </Link>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{customer.domain || 'N/A'}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                            customer.status === 'Active'
                              ? 'bg-green-100 text-green-800'
                              : customer.status === 'Inactive'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {customer.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 text-right font-medium">
                          {customer.totalLicenses.toLocaleString()}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                          {customer.hasAzureSubscription ? (
                            <span className="inline-flex rounded-full bg-sky-100 px-2 text-xs font-semibold leading-5 text-sky-800">
                              Azure
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <Link
                            to={`/customers/${customer.id}`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            View Details
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
